import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { mockUploadFile } from "@/lib/catalog/mockCatalogApi";
import { PRODUCT_GALLERY_ATTR, PRODUCT_IMAGE_MAX_BYTES } from "@/lib/catalog/productMedia";
import { catalogProductErrorMessage, uploadProductImage } from "@/lib/catalog/productsStore";
import type { UploadedFile } from "@/lib/catalog/types";
import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";
import { cn } from "@/lib/utils";
import { Camera, Check, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type SVGProps } from "react";
import { toast } from "sonner";

type UploadTrack = {
  progress: number;
  status: "uploading" | "error";
  error?: string;
};

type GalleryGridSize = 4 | 6 | 8;

const GALLERY_GRID_OPTIONS: GalleryGridSize[] = [4, 6, 8];
const GALLERY_GRID_LS_KEY = "inventory.productGallery.gridSize.v2";

const GALLERY_GRID_CLASS: Record<GalleryGridSize, string> = {
  4: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4",
  6: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6",
  8: "grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8",
};

function GridDensityIcon({ cols, rows, ...props }: SVGProps<SVGSVGElement> & { cols: number; rows: number }) {
  const gap = 1.5;
  const pad = 2;
  const cellW = (16 - pad * 2 - gap * (cols - 1)) / cols;
  const cellH = (16 - pad * 2 - gap * (rows - 1)) / rows;
  const cells: { x: number; y: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({ x: pad + c * (cellW + gap), y: pad + r * (cellH + gap) });
    }
  }
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden {...props}>
      {cells.map((cell, i) => (
        <rect
          key={i}
          x={cell.x}
          y={cell.y}
          width={cellW}
          height={cellH}
          rx={0.6}
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

const GALLERY_GRID_ICON: Record<GalleryGridSize, { cols: number; rows: number; label: string }> = {
  4: { cols: 2, rows: 2, label: "4 per row" },
  6: { cols: 3, rows: 2, label: "6 per row" },
  8: { cols: 4, rows: 2, label: "8 per row" },
};

function readStoredGalleryGridSize(): GalleryGridSize {
  try {
    const raw = localStorage.getItem(GALLERY_GRID_LS_KEY);
    const n = Number(raw);
    if (n === 4 || n === 6 || n === 8) return n;
  } catch {
    /* ignore */
  }
  return 8;
}

type ProductImageGalleryProps = {
  images: UploadedFile[];
  onChange: (images: UploadedFile[]) => void;
  /** When set, each file uploads immediately (one request per image). */
  productId?: string | null;
  disabled?: boolean;
  className?: string;
  dropzoneClassName?: string;
};

function reorderImages(images: UploadedFile[], fromId: string, toId: string): UploadedFile[] {
  if (fromId === toId) return images;
  const fromIndex = images.findIndex((img) => img.id === fromId);
  const toIndex = images.findIndex((img) => img.id === toId);
  if (fromIndex < 0 || toIndex < 0) return images;
  const next = [...images];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function ProductImageGallery({
  images,
  onChange,
  productId,
  disabled = false,
  className,
  dropzoneClassName,
}: ProductImageGalleryProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [dropActive, setDropActive] = useState(false);
  const [dragImageId, setDragImageId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [uploadTracks, setUploadTracks] = useState<Record<string, UploadTrack>>({});
  const [batchUpload, setBatchUpload] = useState<{ completed: number; total: number } | null>(null);
  const [gridSize, setGridSize] = useState<GalleryGridSize>(readStoredGalleryGridSize);
  const uploadingRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(GALLERY_GRID_LS_KEY, String(gridSize));
    } catch {
      /* ignore */
    }
  }, [gridSize]);

  const setTrack = useCallback((id: string, patch: UploadTrack | null) => {
    setUploadTracks((prev) => {
      if (patch === null) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: patch };
    });
  }, []);

  const uploadOne = useCallback(
    async (tempId: string, file: File): Promise<UploadedFile | null> => {
      setTrack(tempId, { progress: 0, status: "uploading" });
      try {
        if (productId) {
          const uploaded = await uploadProductImage(productId, file, (pct) => {
            setTrack(tempId, { progress: pct, status: "uploading" });
          });
          setTrack(tempId, null);
          return uploaded;
        }
        const local = await mockUploadFile(file, PRODUCT_GALLERY_ATTR);
        setTrack(tempId, null);
        return local;
      } catch (e) {
        const message = catalogProductErrorMessage(e);
        setTrack(tempId, { progress: 0, status: "error", error: message });
        toast.error(message);
        return null;
      }
    },
    [productId, setTrack],
  );

  const ingestFiles = useCallback(
    async (fileList: File[]) => {
      if (disabled || uploadingRef.current) return;
      const good = fileList.filter((f) => f.type.startsWith("image/") && f.size <= PRODUCT_IMAGE_MAX_BYTES);
      const skipped = fileList.length - good.length;
      if (skipped > 0) {
        toast.error(`${skipped} file(s) skipped — use images up to 10MB each.`);
      }
      if (good.length === 0) return;

      uploadingRef.current = true;
      setBatchUpload({ completed: 0, total: good.length });

      const placeholders: UploadedFile[] = good.map((file) => ({
        id: crypto.randomUUID(),
        filename: file.name,
        mimeType: file.type || "image/*",
        size: file.size,
        visibility: "public",
        url: URL.createObjectURL(file),
        localFile: productId ? undefined : file,
      }));

      let working = [...images, ...placeholders];
      onChange(working);

      let completed = 0;
      for (let i = 0; i < good.length; i++) {
        const file = good[i];
        const placeholder = placeholders[i];
        const uploaded = await uploadOne(placeholder.id, file);
        completed += 1;
        setBatchUpload({ completed, total: good.length });

        working = working.filter((img) => img.id !== placeholder.id);
        if (uploaded) {
          working = [...working, uploaded];
        }
        onChange(working);
      }

      setBatchUpload(null);
      uploadingRef.current = false;
      if (completed > 0 && completed === good.length) {
        toast.success(completed === 1 ? "Image uploaded." : `${completed} images uploaded.`);
      }
    },
    [disabled, images, onChange, productId, uploadOne],
  );

  const isUploading = batchUpload !== null;

  return (
    <div className={cn("space-y-3", className)}>
      <input
        ref={imageInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/*"
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.currentTarget.value = "";
          void ingestFiles(files);
        }}
      />

      <button
        type="button"
        disabled={disabled || isUploading}
        className={cn(
          "w-full rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-60",
          dropActive ? "border-primary bg-primary/10" : "border-border bg-muted/20 hover:bg-muted/35 hover:border-primary/45",
          dropzoneClassName,
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled && !isUploading) setDropActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          const next = e.relatedTarget as Node | null;
          if (!next || !e.currentTarget.contains(next)) setDropActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDropActive(false);
          if (disabled || isUploading) return;
          void ingestFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => imageInputRef.current?.click()}
      >
        <Camera className="w-9 h-9 mx-auto text-muted-foreground/80 mb-2" aria-hidden />
        <p className="text-sm font-medium text-foreground">Drop images here or browse (multiple)</p>
        <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP — up to 10MB each · no limit on count</p>
      </button>

      {images.length > 0 ? (
        <div className="space-y-2 pt-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">Drag any image to reorder the gallery.</p>
            <div
              className="inline-flex items-center gap-0.5 rounded-md border border-border/60 bg-background/80 p-0.5 shadow-sm"
              role="group"
              aria-label="Gallery grid size"
            >
              {GALLERY_GRID_OPTIONS.map((size) => {
                const icon = GALLERY_GRID_ICON[size];
                return (
                  <button
                    key={size}
                    type="button"
                    className={cn(
                      "inline-flex h-7 w-7 items-center justify-center rounded transition-colors",
                      gridSize === size
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    )}
                    aria-pressed={gridSize === size}
                    aria-label={icon.label}
                    title={icon.label}
                    onClick={() => setGridSize(size)}
                  >
                    <GridDensityIcon cols={icon.cols} rows={icon.rows} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className={cn("grid gap-3", GALLERY_GRID_CLASS[gridSize])}>
            {images.map((u, index) => {
              const track = uploadTracks[u.id];
              const isDragging = dragImageId === u.id;
              const isDropTarget = dropTargetId === u.id && dragImageId !== u.id;
              const previewUrl = normalizeProductMediaUrlForBrowser(u.url) ?? u.url;
              const canReorder = !disabled && !isUploading;

              return (
                <div
                  key={u.id}
                  draggable={canReorder}
                  className={cn(
                    "relative group aspect-[4/3] rounded-lg overflow-hidden border bg-muted/30 transition-shadow",
                    canReorder && "cursor-grab active:cursor-grabbing",
                    isDragging && "opacity-50 ring-2 ring-primary/40",
                    isDropTarget && "ring-2 ring-primary shadow-md",
                    track?.status === "error" ? "border-destructive/60" : "border-border",
                  )}
                  onDragStart={(e) => {
                    if (!canReorder) {
                      e.preventDefault();
                      return;
                    }
                    setDragImageId(u.id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", u.id);
                  }}
                  onDragEnd={() => {
                    setDragImageId(null);
                    setDropTargetId(null);
                  }}
                  onDragOver={(e) => {
                    if (!dragImageId || dragImageId === u.id) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDropTargetId(u.id);
                  }}
                  onDragLeave={() => {
                    if (dropTargetId === u.id) setDropTargetId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragImageId && dragImageId !== u.id) {
                      onChange(reorderImages(images, dragImageId, u.id));
                    }
                    setDragImageId(null);
                    setDropTargetId(null);
                  }}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground p-2 text-center">
                      {u.filename}
                    </div>
                  )}

                  <span className="absolute top-1.5 left-1.5 rounded bg-background/85 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground shadow-sm pointer-events-none">
                    {index + 1}
                  </span>

                  {track?.status === "uploading" ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-end bg-background/55 p-2 gap-1.5 pointer-events-none">
                      <Progress value={track.progress} className="h-1.5 w-full bg-background/80" />
                      <span className="text-[10px] font-medium text-foreground tabular-nums">{track.progress}%</span>
                    </div>
                  ) : null}

                  {track?.status === "error" ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-destructive/15 p-2 text-center pointer-events-none">
                      <span className="text-[10px] font-medium text-destructive line-clamp-3">{track.error ?? "Upload failed"}</span>
                    </div>
                  ) : null}

                  {!track ? (
                    <span
                      className="absolute top-1.5 right-1.5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white shadow-md ring-2 ring-white/90 pointer-events-none"
                      title="Uploaded"
                      aria-label="Uploaded"
                    >
                      <Check className="h-4 w-4 stroke-[2.5]" aria-hidden />
                    </span>
                  ) : null}

                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute bottom-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={disabled || isUploading}
                    onPointerDown={(ev) => {
                      // Keep remove clickable without starting a reorder drag.
                      ev.stopPropagation();
                      ev.preventDefault();
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onChange(images.filter((x) => x.id !== u.id));
                    }}
                    aria-label={`Remove ${u.filename}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
